<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">General expenses</h1>
            <p class="text-muted mb-0">
              Manage your business expenses and track spending
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton
              color="primary"
              variant="outline"
              @click="showBulkEntryModal = true"
              class="mr-2"
            >
              <CIcon name="cil-plus" />
              Bulk entry
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Create expense
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Expense Insights -->
    <CRow class="mb-4">
      <!-- Expense Summary -->
      <CCol lg="8">
        <CCard class="h-100 border-0 shadow-sm">
          <CCardHeader
            class="bg-transparent border-0 d-flex justify-content-between align-items-center p-4"
          >
            <h5 class="mb-0">Expense summary</h5>
          </CCardHeader>
          <CCardBody class="pt-0 pb-4 px-4">
            <CRow class="mb-4">
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Total expenses</span>
                    <span class="inv-kpi-chip chip-navy"><CIcon name="cil-list" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ expenseSummary.totalCount }}</div>
                  <div class="inv-kpi-sub" :class="expenseSummary.countChange >= 0 ? 'trend-up' : 'trend-down'">
                    <CIcon :name="expenseSummary.countChange >= 0 ? 'cil-arrow-top' : 'cil-arrow-bottom'" />
                    {{ Math.abs(expenseSummary.countChange) }}% vs last month
                  </div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Paid</span>
                    <span class="inv-kpi-chip chip-success"><CIcon name="cil-check-circle" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(expenseSummary.paidAmount) }}</div>
                  <div class="inv-kpi-sub">{{ expenseSummary.paidCount }} expense{{ expenseSummary.paidCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Approved</span>
                    <span class="inv-kpi-chip chip-warning"><CIcon name="cil-clock" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(expenseSummary.approvedAmount) }}</div>
                  <div class="inv-kpi-sub">{{ expenseSummary.approvedCount }} expense{{ expenseSummary.approvedCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Draft</span>
                    <span class="inv-kpi-chip chip-secondary"><CIcon name="cil-pencil" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(expenseSummary.draftAmount) }}</div>
                  <div class="inv-kpi-sub">{{ expenseSummary.draftCount }} expense{{ expenseSummary.draftCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
            </CRow>
            <ExpenseTrendChart
              :chartData="expenseTrendData"
              style="height: 250px"
            />
          </CCardBody>
        </CCard>
      </CCol>
      <!-- Status Distribution -->
      <CCol lg="4">
        <CCard class="h-100 border-0 shadow-sm">
          <CCardHeader
            class="bg-transparent border-0 d-flex justify-content-between align-items-center p-4"
          >
            <h5 class="mb-0">Status distribution</h5>
          </CCardHeader>
          <CCardBody
            class="pt-0 pb-4 px-4 d-flex flex-column align-items-center justify-content-center"
          >
            <div style="height: 200px; width: 200px" class="mb-4">
              <ExpenseStatusChart :chartData="statusDistributionData" />
            </div>
            <div class="w-100">
              <div
                v-for="status in statusDistribution"
                :key="status.name"
                class="d-flex align-items-center mb-2"
              >
                <div
                  class="mr-2 status-dot"
                  :class="'bg-' + badgeColor('expense', status.name)"
                ></div>
                <div class="flex-grow-1">{{ badgeLabel('expense', status.name) }}</div>
                <div class="text-right">
                  <strong>{{ status.count }}</strong>
                  <span class="text-muted ml-1"
                    >({{ status.percentage }}%)</span
                  >
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Expense List -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardHeader class="bg-transparent border-0 p-4">
        <CRow class="align-items-center">
          <CCol>
            <h5 class="mb-0">
              Expenses
              <CBadge color="light" class="ml-2">{{ totalItems }}</CBadge>
            </h5>
          </CCol>
        </CRow>
      </CCardHeader>
      <CCardBody class="p-4">
        <!-- Advanced Filters -->
        <CCollapse :show="showFilters">
          <CCard class="mb-4 border">
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <CInput
                    label="Vendor"
                    v-model="filters.vendorName"
                    placeholder="Search vendor"
                    @keyup.enter="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <CInput
                    label="Description"
                    v-model="filters.description"
                    placeholder="Search description"
                    @keyup.enter="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <CSelect
                    label="Status"
                    v-model="filters.status"
                    @change="applyFilters"
                  >
                    <option value="">All statuses</option>
                    <option
                      v-for="status in statusOptions"
                      :key="status.id"
                      :value="status.name"
                    >
                      {{ badgeLabel('expense', status.name) }}
                    </option>
                  </CSelect>
                </CCol>
                <CCol md="3">
                  <CInput
                    type="date"
                    label="From"
                    v-model="filters.dateFrom"
                    class="mb-3"
                    @change="applyFilters"
                  />
                  <CInput
                    type="date"
                    label="To"
                    v-model="filters.dateTo"
                    @change="applyFilters"
                  />
                </CCol>
              </CRow>
              <CRow class="mt-3">
                <CCol class="d-flex justify-content-end">
                  <CButton color="light" class="mr-2" @click="resetFilters">
                    <CIcon name="cil-x" /> Clear filters
                  </CButton>
                  <CButton color="primary" @click="applyFilters">
                    <CIcon name="cil-filter" /> Apply filters
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCollapse>

        <!-- Enhanced toolbar -->
        <CRow class="mb-4 align-items-center">
          <CCol xs="12" class="mb-3 mb-md-0">
            <CButtonGroup>
              <CButton color="light" @click="showFilters = !showFilters">
                <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
                {{ showFilters ? "Hide filters" : "Show filters" }}
              </CButton>
              <CBadge
                v-if="hasActiveFilters"
                color="primary"
                class="ml-2 align-self-center"
              >
                {{ activeFiltersCount }} active
              </CBadge>
            </CButtonGroup>
          </CCol>
        </CRow>

        <!-- Filtered Results Summary -->
        <CCard v-if="!loading && !loadError && computedItems.length > 0" class="mb-3 inv-summary-card d-none d-lg-block">
          <CCardBody class="p-3">
            <div class="d-flex align-items-center mb-3">
              <CIcon name="cil-calculator" class="mr-2 text-brand-navy" />
              <span class="inv-summary-title">Filtered results summary</span>
              <CBadge class="ml-2 badge-soft">{{ filteredSummary.totalCount }} expense{{ filteredSummary.totalCount !== 1 ? 's' : '' }}</CBadge>
            </div>
            <CRow>
              <CCol lg="3" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Total</div><div class="inv-stat-val text-brand-navy">{{ formatCurrency(filteredSummary.totalAmount) }}</div></div>
              </CCol>
              <CCol lg="3" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Paid</div><div class="inv-stat-val text-success-strong">{{ formatCurrency(filteredSummary.paidAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.paidCount }} expense{{ filteredSummary.paidCount !== 1 ? 's' : '' }}</div></div>
              </CCol>
              <CCol lg="3" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Unpaid</div><div class="inv-stat-val text-danger-strong">{{ formatCurrency(filteredSummary.unpaidAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.unpaidCount }} expense{{ filteredSummary.unpaidCount !== 1 ? 's' : '' }}</div></div>
              </CCol>
              <CCol lg="3" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Average</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.averageAmount) }}</div><div class="inv-stat-meta">per expense</div></div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Desktop View -->
        <div class="d-none d-lg-block">
          <div v-if="loading && !items.length" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading…</span>
            </div>
            <p class="mt-3 text-muted">Loading expenses…</p>
          </div>

          <div v-else-if="loadError" class="text-center py-5">
            <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
            <h5>Couldn't load expenses</h5>
            <p class="text-muted mb-3">Check your connection and try again.</p>
            <CButton color="primary" @click="refreshTable">
              <CIcon name="cil-reload" /> Try again
            </CButton>
          </div>

          <CDataTable
            v-else
            :items="computedItems"
            :fields="fields"
            :loading="loading"
            :pagination="{
              align: 'end',
              doubleArrows: true,
            }"
            :sorter="{ external: true }"
            :table-filter="false"
            :items-per-page-select="{
              values: [10, 25, 50, 100],
              label: 'Items per page:',
            }"
            :items-per-page="perPage"
            :active-page="currentPage"
            :pages="totalPages"
            hover
            striped
            bordered
            responsive
            @pagination-change="onPaginationChange"
            @items-per-page-change="onItemsPerPageChange"
            @sorter-value-change="onSorterChange"
          >
            <!-- Index Column -->
            <template #show_index="{ index }">
              <td class="py-2 text-muted">
                {{ (currentPage - 1) * perPage + index + 1 }}
              </td>
            </template>

            <!-- Date Column -->
            <template #displayDate="{ item }">
              <td class="py-2">
                <div>{{ item.displayDate }}</div>
              </td>
            </template>

            <!-- Vendor Column with Details -->
            <template #vendorName="{ item }">
              <td class="py-2">
                <div class="d-flex align-items-center">
                  <div class="vendor-avatar mr-2">
                    <div
                      class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center small"
                      style="width: 32px; height: 32px"
                    >
                      {{ getVendorInitials(item.vendorName) }}
                    </div>
                  </div>
                  <div>
                    <div class="font-weight-medium">{{ item.vendorName }}</div>
                    <small class="text-muted">{{
                      getTruncatedDescription(item.description)
                    }}</small>
                  </div>
                </div>
              </td>
            </template>

            <!-- Amount Column -->
            <template #totalAmount="{ item }">
              <td class="py-2 text-right">
                <div class="font-weight-bold text-dark">
                  {{ formatCurrency(item.totalAmount) }}
                </div>
              </td>
            </template>

            <!-- Status Column -->
            <template #show_status="{ item }">
              <td class="py-2">
                <CBadge
                  :color="badgeColor('expense', item.expenseStateDescription)"
                  class="px-2 py-1"
                >
                  {{ badgeLabel('expense', item.expenseStateDescription) }}
                </CBadge>
              </td>
            </template>

            <template #show_payment_status="{ item }">
              <td class="py-2">
                <div v-if="item.expenseStateDescription === 'Paid'">
                  <CBadge :color="badgeColor('documentPayment', 'Paid')" class="px-2 py-1 mb-1">
                    {{ badgeLabel('documentPayment', 'Paid') }}
                  </CBadge>
                  <div class="small text-muted" v-if="item.paymentDate">
                    {{ getDisplayDate(item.paymentDate) }}
                  </div>
                  <div class="small text-muted" v-if="item.paymentMethod">
                    via {{ item.paymentMethod }}
                  </div>
                </div>
                <div v-else-if="item.expenseStateDescription === 'Approve'">
                  <CBadge :color="badgeColor('documentPayment', 'Unpaid')" class="px-2 py-1">
                    {{ badgeLabel('documentPayment', 'Unpaid') }}
                  </CBadge>
                </div>
                <div v-else>
                  <span class="text-muted">—</span>
                </div>
              </td>
            </template>

            <!-- Actions Column -->
            <template #show_details="{ item }">
              <td class="py-2 position-relative">
                <div class="text-right action-buttons">
                  <CButtonGroup>
                    <CButton
                      color="light"
                      size="sm"
                      @click="onEdit(item)"
                      title="Edit"
                      aria-label="Edit expense"
                      class="border"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>

                    <CDropdown
                      size="sm"
                      color="light"
                      placement="bottom-end"
                      class="dropdown-with-zindex border"
                    >
                      <template #toggler-content>
                        <CIcon name="cil-options" />
                        <span class="sr-only">More actions</span>
                      </template>
                      <CDropdownItem @click="onEdit(item)">
                        <CIcon name="cil-pencil" class="mr-2" /> Edit
                      </CDropdownItem>

                      <CDropdownItem @click="onDuplicate(item)">
                        <CIcon name="cil-copy" class="mr-2" /> Duplicate
                      </CDropdownItem>
                      <CDropdownItem
                        v-if="item.expenseStateDescription === 'Draft'"
                        @click="askAction('approve', item)"
                      >
                        <CIcon name="cil-check" class="mr-2" /> Approve
                      </CDropdownItem>
                      <CDropdownItem
                        v-if="item.expenseStateDescription === 'Approve'"
                        @click="askAction('pay', item)"
                      >
                        <CIcon name="cil-credit-card" class="mr-2" /> Mark as paid
                      </CDropdownItem>
                      <CDropdownDivider />
                      <CDropdownItem
                        @click="showDeleteConfirmation(item)"
                        class="text-danger"
                      >
                        <CIcon name="cil-trash" class="mr-2" /> Delete
                      </CDropdownItem>
                    </CDropdown>
                  </CButtonGroup>
                </div>
              </td>
            </template>

            <!-- Empty State -->
            <template #no-items-view>
              <div v-if="hasActiveFilters" class="text-center py-5">
                <CIcon name="cil-filter-x" size="2xl" class="mb-3 text-muted" />
                <h5>No expenses match your filters</h5>
                <CButton color="light" class="mt-2" @click="resetFilters">
                  <CIcon name="cil-x" /> Clear filters
                </CButton>
              </div>
              <div v-else class="text-center py-5">
                <CIcon name="cil-inbox" size="2xl" class="mb-3 text-muted" />
                <h5>No expenses yet</h5>
                <p class="text-muted mb-3">Record what your business spends so you can track it here.</p>
                <CButton color="primary" @click="addNew">
                  <CIcon name="cil-plus" /> Create expense
                </CButton>
              </div>
            </template>
          </CDataTable>

          <!-- Pagination Info -->
          <CRow class="mt-2" v-if="!loading && totalItems > 0">
            <CCol md="6" class="text-muted text-center">
              <small>
                Total: {{ totalItems }} expenses | Pages: {{ totalPages }} |
                Current: {{ currentPage }} | Per Page: {{ perPage }}
                <span v-if="hasActiveFilters">(filtered)</span>
              </small>
            </CCol>
            <CCol md="6" class="text-center">
              <CPagination
                v-if="totalPages > 1"
                :activePage="currentPage"
                :pages="totalPages"
                @update:activePage="onPageChange"
                size="sm"
                class="mb-0"
              />
            </CCol>
          </CRow>
        </div>

        <!-- Mobile/Tablet View -->
        <div class="d-lg-none">
          <!-- Mobile Loading -->
          <div v-if="loading && !items.length" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading…</span>
            </div>
            <p class="mt-3 text-muted">Loading expenses…</p>
          </div>

          <!-- Mobile Error -->
          <div v-else-if="loadError" class="text-center py-5">
            <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
            <h5>Couldn't load expenses</h5>
            <p class="text-muted mb-3">Check your connection and try again.</p>
            <CButton color="primary" @click="refreshTable">
              <CIcon name="cil-reload" /> Try again
            </CButton>
          </div>

          <!-- Mobile Empty (filtered) -->
          <div v-else-if="computedItems.length === 0 && hasActiveFilters" class="text-center py-5">
            <CIcon name="cil-filter-x" size="2xl" class="mb-3 text-muted" />
            <h5>No expenses match your filters</h5>
            <CButton color="light" class="mt-2" @click="resetFilters">
              <CIcon name="cil-x" /> Clear filters
            </CButton>
          </div>

          <!-- Mobile Empty (first use) -->
          <div v-else-if="computedItems.length === 0" class="text-center py-5">
            <CIcon name="cil-inbox" size="2xl" class="mb-3 text-muted" />
            <h5>No expenses yet</h5>
            <p class="text-muted mb-3">Record what your business spends so you can track it here.</p>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Create expense
            </CButton>
          </div>

          <template v-else>
          <!-- Mobile Filtered Summary -->
          <CCard class="mb-3 inv-summary-card">
            <CCardBody class="p-2">
              <div class="d-flex align-items-center mb-2">
                <CIcon name="cil-calculator" size="sm" class="mr-1 text-brand-navy" />
                <span class="inv-summary-title">Summary</span>
                <CBadge class="ml-1 badge-soft">{{ filteredSummary.totalCount }}</CBadge>
              </div>
              <CRow class="text-center">
                <CCol xs="6" class="mb-2"><div class="inv-stat-mini"><div class="inv-stat-label">Total</div><div class="inv-stat-val text-brand-navy">{{ formatCurrency(filteredSummary.totalAmount) }}</div></div></CCol>
                <CCol xs="6" class="mb-2"><div class="inv-stat-mini"><div class="inv-stat-label">Average</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.averageAmount) }}</div></div></CCol>
                <CCol xs="6"><div class="inv-stat-mini"><div class="inv-stat-label">Paid</div><div class="inv-stat-val text-success-strong">{{ formatCurrency(filteredSummary.paidAmount) }}</div></div></CCol>
                <CCol xs="6"><div class="inv-stat-mini"><div class="inv-stat-label">Unpaid</div><div class="inv-stat-val text-danger-strong">{{ formatCurrency(filteredSummary.unpaidAmount) }}</div></div></CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Mobile Items per page selector -->
          <CRow class="mb-3">
            <CCol xs="6">
              <CSelect v-model="perPage" @change="onItemsPerPageChange">
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
              </CSelect>
            </CCol>
            <CCol
              xs="6"
              class="text-right text-muted d-flex align-items-center justify-content-end"
            >
              {{ paginationStart }}-{{ paginationEnd }} of {{ totalItems }}
            </CCol>
          </CRow>

          <!-- Mobile Cards -->
          <CCard
            v-for="(item, index) in computedItems"
            :key="item.id"
            class="mb-3 border-0 shadow-sm"
          >
            <CCardHeader
              class="d-flex justify-content-between align-items-center py-3 bg-light"
            >
              <div>
                <strong>{{ item.vendorName }}</strong>
                <small class="d-block text-muted">{{ item.displayDate }}</small>
              </div>
              <CBadge
                :color="badgeColor('expense', item.expenseStateDescription)"
                class="px-2 py-1"
              >
                {{ badgeLabel('expense', item.expenseStateDescription) }}
              </CBadge>
            </CCardHeader>
            <CCardBody class="py-3">
              <CRow>
                <CCol xs="6">
                  <div class="mb-2">
                    <div class="text-muted small">Date</div>
                    <div>{{ item.displayDate }}</div>
                  </div>
                  <div class="mb-2">
                    <div class="text-muted small">Description</div>
                    <div>{{ getTruncatedDescription(item.description) }}</div>
                  </div>
                </CCol>
                <CCol xs="6">
                  <div class="mb-2">
                    <div class="text-muted small">Amount</div>
                    <div class="font-weight-bold h6">
                      {{ formatCurrency(item.totalAmount) }}
                    </div>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
            <CCardFooter class="py-2 d-flex justify-content-between bg-light">
              <CButton color="light" size="sm" @click="onEdit(item)">
                <CIcon name="cil-pencil" /> Edit
              </CButton>
              <CDropdown
                size="sm"
                color="light"
                placement="bottom-end"
                class="dropdown-mobile border"
              >
                <template #toggler-content>
                  <CIcon name="cil-options" /> More
                </template>
                <CDropdownItem @click="onDuplicate(item)">
                  <CIcon name="cil-copy" class="mr-2" /> Duplicate
                </CDropdownItem>
                <CDropdownItem
                  v-if="item.expenseStateDescription === 'Draft'"
                  @click="askAction('approve', item)"
                >
                  <CIcon name="cil-check" class="mr-2" /> Approve
                </CDropdownItem>
                <CDropdownItem
                  v-if="item.expenseStateDescription === 'Approve'"
                  @click="askAction('pay', item)"
                >
                  <CIcon name="cil-credit-card" class="mr-2" /> Mark as paid
                </CDropdownItem>
                <CDropdownDivider />
                <CDropdownItem
                  @click="showDeleteConfirmation(item)"
                  class="text-danger"
                >
                  <CIcon name="cil-trash" class="mr-2" /> Delete
                </CDropdownItem>
              </CDropdown>
            </CCardFooter>
          </CCard>

          <!-- Mobile Pagination -->
          <CRow class="mt-4">
            <CCol xs="12" class="d-flex justify-content-center">
              <CPagination
                :activePage="currentPage"
                :pages="totalPages"
                @update:activePage="onPageChange"
                size="sm"
              />
            </CCol>
          </CRow>
          </template>
        </div>
      </CCardBody>
    </CCard>

    <!-- Delete Confirmation Modal -->
    <CModal
      :title="'Delete the expense from ' + (itemToDelete.vendorName || 'this vendor') + '?'"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
      @update:show="onDeleteConfirmation"
    >
      <p class="mb-0">
        The {{ formatCurrency(itemToDelete.totalAmount || 0) }} expense dated
        {{ getDisplayDate(itemToDelete.date) }} will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          {{ deleting ? "Deleting…" : "Delete expense" }}
        </CButton>
      </template>
    </CModal>

    <!-- Approve / mark-as-paid confirmation -->
    <CModal
      :title="actionConfirmTitle"
      :show.sync="actionConfirm.show"
      :close-on-backdrop="!actionConfirm.busy"
    >
      <p class="mb-0">{{ actionConfirmMessage }}</p>
      <template #footer>
        <CButton color="light" :disabled="actionConfirm.busy" @click="actionConfirm.show = false">Cancel</CButton>
        <CButton color="primary" :disabled="actionConfirm.busy" @click="runAction">
          <CSpinner v-if="actionConfirm.busy" size="sm" class="mr-1" />
          {{ actionConfirm.kind === "pay" ? "Mark as paid" : "Approve expense" }}
        </CButton>
      </template>
    </CModal>

    <!-- Bulk entry: discard / clear confirmation -->
    <CModal
      :title="bulkConfirm === 'clear' ? 'Clear all rows?' : 'Discard these entries?'"
      :show="!!bulkConfirm"
      @update:show="onBulkConfirmShow"
    >
      <p class="mb-0">
        The {{ bulkExpenses.length }} row{{ bulkExpenses.length === 1 ? "" : "s" }} you've entered will be removed.
        Nothing has been saved yet.
      </p>
      <template #footer>
        <CButton color="light" @click="bulkConfirm = null">Keep editing</CButton>
        <CButton color="danger" @click="confirmBulkDiscard">
          {{ bulkConfirm === "clear" ? "Clear rows" : "Discard entries" }}
        </CButton>
      </template>
    </CModal>

    <!-- Quick Info Alert -->
    <CAlert color="info" closeButton :show.sync="showQuickInfo">
      <h6><CIcon name="cil-info" /> Quick tips</h6>
      <ul class="mb-0">
        <li>Use filters to find specific expenses</li>
        <li>Track spending trends in the summary section</li>
        <li>Quick actions for common tasks</li>
      </ul>
    </CAlert>

    <!-- ✨ NEW: Bulk Entry Modal -->
  <CModal
    title="Add several expenses"
    :show="showBulkEntryModal"
    size="xl"
    @update:show="onBulkModalClose"
  >
    <!-- Header with instructions -->
    <CAlert color="info" class="mb-3">
      <CIcon name="cil-info" class="mr-2" />
      Add several expenses at once. They are all created as drafts.
    </CAlert>

    <!-- Bulk Entry Table -->
    <CCard>
      <CCardHeader class="d-flex justify-content-between align-items-center">
        <h6 class="mb-0">Expense entries</h6>
        <div>
          <CButton 
            color="primary" 
            size="sm" 
            @click="addBulkRow"
            class="mr-2"
          >
            <CIcon name="cil-plus" class="mr-1" />
            Add row
          </CButton>
          <CButton
            color="light"
            size="sm"
            @click="clearAllRows"
            :disabled="bulkExpenses.length === 0"
          >
            <CIcon name="cil-trash" class="mr-1" />
            Clear all
          </CButton>
        </div>
      </CCardHeader>
      
      <CCardBody class="p-0">
        <div class="table-responsive">
          <table class="table table-bordered mb-0">
            <thead class="thead-light">
              <tr>
                <th style="width: 40px">#</th>
                <th style="width: 150px">Date *</th>
                <th style="width: 200px">Vendor *</th>
                <th style="width: 120px">Amount (RM) *</th>
                <th>Description</th>
                <th style="width: 60px">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="bulkExpenses.length === 0">
                <td colspan="6" class="text-center text-muted py-4">
                  <CIcon name="cil-info" class="mr-2" />
                  No entries yet. Choose "Add row" to start.
                </td>
              </tr>
              <tr 
                v-for="(expense, index) in bulkExpenses" 
                :key="`bulk-${index}`"
                :class="{ 'table-danger': hasRowErrors(index) }"
              >
                <!-- Row Number -->
                <td class="text-center align-middle">
                  <strong>{{ index + 1 }}</strong>
                </td>
                
                <!-- Date -->
                <td>
                  <input 
                    v-model="expense.date"
                    type="date" 
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': !expense.date }"
                    aria-label="Date"
                  />
                </td>
                
                <!-- Vendor Selection -->
                <td>
                  <select 
                    v-model="expense.vendorId" 
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': !expense.vendorId }"
                    aria-label="Vendor"
                    @change="onVendorChange(index)"
                  >
                    <option value="">Select vendor</option>
                    <optgroup label="Frequent vendors" v-if="quickVendors.length > 0">
                      <option 
                        v-for="vendor in quickVendors" 
                        :key="`quick-${vendor.vendorId}`"
                        :value="vendor.vendorId"
                      >
                        {{ vendor.vendorName }} (avg RM {{ formatMyr(vendor.averageAmount) }})
                      </option>
                    </optgroup>
                    <optgroup label="All vendors" v-if="allVendors.length > 0">
                      <option 
                        v-for="vendor in allVendors" 
                        :key="`all-${vendor.id}`"
                        :value="vendor.id"
                      >
                        {{ vendor.name }}
                      </option>
                    </optgroup>
                  </select>
                </td>
                
                <!-- Amount -->
                <td>
                  <input 
                    v-model="expense.amount"
                    type="number"
                    inputmode="decimal"
                    aria-label="Amount (RM)"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    class="form-control form-control-sm text-right"
                    :class="{ 'is-invalid': !expense.amount || parseFloat(expense.amount) <= 0 }"
                  />
                </td>
                
                <!-- Description -->
                <td>
                  <input 
                    v-model="expense.description"
                    type="text" 
                    placeholder="Description"
                    aria-label="Description"
                    class="form-control form-control-sm"
                  />
                </td>
                
                <!-- Actions -->
                <td class="text-center">
                  <CButton 
                    color="danger" 
                    size="sm" 
                    variant="outline"
                    @click="removeBulkRow(index)"
                    v-c-tooltip="'Remove row'"
                    aria-label="Remove row"
                  >
                    <CIcon name="cil-trash" size="sm" />
                  </CButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>

    <!-- Summary Card -->
    <CCard class="mt-3" v-if="bulkExpenses.length > 0">
      <CCardBody>
        <CRow>
          <CCol cols="3">
            <div class="text-center">
              <h4 class="text-primary mb-1">{{ bulkExpenses.length }}</h4>
              <small class="text-muted">Total entries</small>
            </div>
          </CCol>
          <CCol cols="3">
            <div class="text-center">
              <h4 class="text-success mb-1">{{ validEntries }}</h4>
              <small class="text-muted">Ready to save</small>
            </div>
          </CCol>
          <CCol cols="3">
            <div class="text-center">
              <h4 class="text-danger mb-1">{{ invalidEntries }}</h4>
              <small class="text-muted">Need details</small>
            </div>
          </CCol>
          <CCol cols="3">
            <div class="text-center">
              <h4 class="mb-1">RM {{ formatMyr(totalAmount) }}</h4>
              <small class="text-muted">Total amount</small>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Modal Footer -->
    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <div>
          <small class="text-muted">
            <CIcon name="cil-info" class="mr-1" />
            All expenses will be created as drafts. You can approve them individually later.
          </small>
        </div>
        <div>
          <CButton color="light" :disabled="isSaving" @click="onBulkModalClose(false)">
            Cancel
          </CButton>
          <CButton
            color="primary"
            @click="saveBulkExpenses"
            :disabled="validEntries === 0 || isSaving"
            class="ml-2"
          >
            <CSpinner 
              v-if="isSaving" 
              size="sm" 
              class="mr-2"
            />
            {{ isSaving ? 'Saving…' : `Create ${validEntries} expense${validEntries !== 1 ? 's' : ''}` }}
          </CButton>
        </div>
      </div>
    </template>
  </CModal>
  </div>
</template>

<script>
import ExpenseApi from "@/lib/expenseApi";
import { formatMyr } from "@/utils/money";
import { formatDate, mytToday, asDateString } from "@/utils/datetime";
import { badgeColor, badgeLabel, badgeChartColor } from "@/utils/statusBadges";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// The last `count` calendar months in Malaysia time, oldest first: [{ ym: "2026-06", label: "Jun" }, ...]
function lastMonths(count) {
  const [y, m] = mytToday().split("-").map(Number);
  const out = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    const mm = d.getUTCMonth();
    out.push({ ym: `${d.getUTCFullYear()}-${String(mm + 1).padStart(2, "0")}`, label: MONTH_NAMES[mm] });
  }
  return out;
}
import ExpenseTrendChart from "../widgets/ExpenseTrendChart";
import ExpenseStatusChart from "../widgets/ExpenseStatusChart";
import VendorApi from "@/lib/vendorApi";

export default {
  name: "OtherExpenseList",
  components: {
    ExpenseTrendChart,
    ExpenseStatusChart,
  },
  data() {
    return {
      vendorApi: new VendorApi(),

      showQuickInfo: false,
      loading: true,
      loadError: false,
      deleting: false,
      actionConfirm: { show: false, kind: "", item: null, busy: false },
      bulkConfirm: null,
      items: [],
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:50px",
          sorter: false,
          filter: false,
        },
        {
          key: "displayDate",
          label: "Date",
          sorter: true,
          filter: true,
        },
        {
          key: "vendorName",
          label: "Vendor",
          sorter: true,
          filter: true,
        },
        {
          key: "totalAmount",
          label: "Amount",
          sorter: true,
          filter: false,
          _style: "text-align: right",
          _classes: "text-right",
        },
        {
          key: "show_status",
          label: "Status",
          sorter: true,
          filter: true,
        },
        {
          key: "show_payment_status",
          label: "Payment",
          _style: "width:180px",
          sorter: false,
          filter: false,
        },
        {
          key: "show_details",
          label: "Actions",
          _style: "width:140px",
          sorter: false,
          filter: false,
        },
      ],
      details: [],
      collapseDuration: 0,
      expenseApi: new ExpenseApi(),
      warningModal: false,
      itemToDelete: {},
      currentPage: 1,
      perPage: 25,
      totalItems: 0,
      totalPages: 1,
      // Sorting
      sortBy: "date",
      sortDirection: "desc",
      // Filters
      showFilters: false,
      filters: {
        vendorName: "",
        description: "",
        status: "",
        dateFrom: "",
        dateTo: "",
      },
      // Status options from API
      statusOptions: [],

      // Real analytics source — all expenses for the org-wide summary
      // (separate one-time pull; the paginated table uses `items`)
      allExpenses: [],

         // Bulk Entry Modal
         showBulkEntryModal: false,
      bulkExpenses: [],
      quickVendors: [],
      allVendors: [],
      isSaving: false,
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Expenses", "General Expenses"]);
    this.loadStatusOptions();
    this.refreshTable();
    this.fetchSummary(); // Load org-wide expenses for real analytics

    // Check for pending payroll expenses
    const pendingData = sessionStorage.getItem('pendingPayrollExpenses');
    if (pendingData && this.$route.query.fromPayroll === 'true') {
      try {
        const expenseEntries = JSON.parse(pendingData);
        this.autoPopulateFromPayroll(expenseEntries);
        sessionStorage.removeItem('pendingPayrollExpenses');
      } catch (error) {
        console.error('Error loading payroll expense data:', error);
        this.$toast.error("Couldn't load the payroll expenses. Open them again from the payroll.");
      }
    }
  },
  watch: {
    showBulkEntryModal(newVal) {
      if (newVal) {
        this.loadBulkEntryData()
        
        // Add a few initial rows
        if (this.bulkExpenses.length === 0) {
          for (let i = 0; i < 3; i++) {
            this.addBulkRow()
          }
        }
      }
    }
  },
  computed: {
    computedItems() {
      return this.items.map((item) => {
        return {
          ...item,
          displayDate: this.getDisplayDate(item.date),
          vendorName: this.getVendorName(item),
        };
      });
    },
    paginationStart() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalItems);
    },
    hasActiveFilters() {
      return Object.values(this.filters).some((filter) => filter !== "");
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter((filter) => filter !== "")
        .length;
    },

    // Filtered expense summary for the current view (paginated table items)
    filteredSummary() {
      const expenses = this.computedItems;
      let totalAmount = 0,
        paidAmount = 0,
        unpaidAmount = 0,
        paidCount = 0,
        unpaidCount = 0;

      expenses.forEach((e) => {
        const amount = parseFloat(e.totalAmount) || 0;
        if (e.expenseStateDescription === "Cancelled") return;
        totalAmount += amount;
        if (e.expenseStateDescription === "Paid" || e.isPaid) {
          paidAmount += amount;
          paidCount++;
        } else if (e.expenseStateDescription === "Approve") {
          unpaidAmount += amount;
          unpaidCount++;
        }
      });

      const counted = expenses.filter(
        (e) => e.expenseStateDescription !== "Cancelled"
      ).length;
      const averageAmount = counted > 0 ? totalAmount / counted : 0;

      return {
        totalAmount,
        paidAmount,
        unpaidAmount,
        paidCount,
        unpaidCount,
        averageAmount,
        totalCount: expenses.length,
      };
    },

    // ===== Real analytics (computed from allExpenses) =====
    // Org-wide summary; keys preserved so the KPI templates stay unchanged.
    expenseSummary() {
      const all = this.allExpenses || [];
      let paidAmount = 0,
        paidCount = 0,
        approvedAmount = 0,
        approvedCount = 0,
        draftAmount = 0,
        draftCount = 0;

      all.forEach((e) => {
        const amount = parseFloat(e.totalAmount) || 0;
        const state = e.expenseStateDescription;
        if (state === "Paid" || e.isPaid) {
          paidAmount += amount;
          paidCount++;
        } else if (state === "Approve") {
          approvedAmount += amount;
          approvedCount++;
        } else if (state === "Draft") {
          draftAmount += amount;
          draftCount++;
        }
      });

      const [prev, current] = lastMonths(2);
      const inMonth = (ym) =>
        all.filter((e) => asDateString(e.date).slice(0, 7) === ym).length;
      const cThis = inMonth(current.ym);
      const cLast = inMonth(prev.ym);
      const countChange = cLast
        ? Math.round(((cThis - cLast) / cLast) * 1000) / 10
        : cThis > 0
        ? 100
        : 0;

      return {
        totalCount: all.length,
        countChange,
        paidAmount,
        paidCount,
        approvedAmount,
        approvedCount,
        draftAmount,
        draftCount,
      };
    },

    // Status distribution by state (org-wide, real)
    statusDistribution() {
      const all = this.allExpenses || [];
      const counts = {};
      all.forEach((e) => {
        const status = e.expenseStateDescription || "Unknown";
        counts[status] = (counts[status] || 0) + 1;
      });
      const total = all.length || 1;
      return Object.keys(counts).map((status) => ({
        name: status,
        count: counts[status],
        percentage: Math.round((counts[status] / total) * 1000) / 10,
      }));
    },

    // Chart data for expense trends — last 6 months, Spend vs Paid (real)
    expenseTrendData() {
      const months = lastMonths(6);
      const all = this.allExpenses || [];
      const spend = months.map((m) =>
        all
          .filter(
            (e) =>
              e.expenseStateDescription !== "Cancelled" &&
              asDateString(e.date).slice(0, 7) === m.ym
          )
          .reduce((a, e) => a + (parseFloat(e.totalAmount) || 0), 0)
      );
      const paid = months.map((m) =>
        all
          .filter(
            (e) =>
              (e.expenseStateDescription === "Paid" || e.isPaid) &&
              asDateString(e.paymentDate || e.date).slice(0, 7) === m.ym
          )
          .reduce((a, e) => a + (parseFloat(e.totalAmount) || 0), 0)
      );
      return {
        labels: months.map((m) => m.label),
        datasets: [
          {
            label: "Spend",
            backgroundColor: "transparent",
            borderColor: badgeChartColor("expense", "Approve"),
            pointBackgroundColor: badgeChartColor("expense", "Approve"),
            data: spend,
          },
          {
            label: "Paid",
            backgroundColor: "transparent",
            borderColor: badgeChartColor("expense", "Paid"),
            pointBackgroundColor: badgeChartColor("expense", "Paid"),
            data: paid,
          },
        ],
      };
    },
    // Chart data for status distribution
    statusDistributionData() {
      return {
        labels: this.statusDistribution.map((status) => badgeLabel("expense", status.name)),
        datasets: [
          {
            data: this.statusDistribution.map((status) => status.count),
            backgroundColor: this.statusDistribution.map((status) =>
              badgeChartColor("expense", status.name)
            ),
          },
        ],
      };
    },
    validEntries() {
      return this.bulkExpenses.filter(expense => 
        expense.date && 
        expense.vendorId && 
        expense.amount && 
        parseFloat(expense.amount) > 0
      ).length
    },
    
    invalidEntries() {
      return this.bulkExpenses.length - this.validEntries
    },
    
    totalAmount() {
      return this.bulkExpenses
        .filter(expense => expense.amount && parseFloat(expense.amount) > 0)
        .reduce((sum, expense) => sum + parseFloat(expense.amount), 0)
    },

    // Rows the user actually typed something into
    hasBulkContent() {
      return this.bulkExpenses.some((e) => e.vendorId || e.amount || e.description);
    },

    actionConfirmTitle() {
      const item = this.actionConfirm.item || {};
      const who = item.vendorName && item.vendorName !== "N/A" ? ` from ${item.vendorName}` : "";
      return this.actionConfirm.kind === "pay"
        ? `Mark the expense${who} as paid?`
        : `Approve the expense${who}?`;
    },

    actionConfirmMessage() {
      const item = this.actionConfirm.item || {};
      const amount = this.formatCurrency(item.totalAmount || 0);
      return this.actionConfirm.kind === "pay"
        ? `The ${amount} expense dated ${this.getDisplayDate(item.date)} will be recorded as paid in your accounts.`
        : `The ${amount} expense dated ${this.getDisplayDate(item.date)} will be approved and posted to your accounts.`;
    },
  },
  methods: {
    autoPopulateFromPayroll(expenseEntries) {
      const self = this;

      try {
        // Wait for vendors to load
        setTimeout(() => {
          // Clear existing bulk expenses
          self.bulkExpenses = [];

          // Add each contribution as a separate row
          expenseEntries.forEach(entry => {
            self.bulkExpenses.push({
              date: entry.date,
              vendorId: '', // User will need to select vendor
              amount: entry.totalAmount,
              description: entry.description,
              chartAccountId: entry.chartAccountId
            });
          });

          // Show the bulk entry modal
          self.showBulkEntryModal = true;

          // Show success notification
          self.$toast.success(
            `${expenseEntries.length} expense entries loaded from payroll ${expenseEntries[0].payrollNo}`
          );

          // Show info about what was loaded
          setTimeout(() => {
            const types = expenseEntries.map(e => e.contributionType).join(', ');
            self.$toast.info(`Loaded ${types} for ${expenseEntries[0].employeeName}`);
          }, 500);

        }, 1500); // Wait for vendors and chart accounts to load

      } catch (error) {
        console.error('Error auto-populating from payroll:', error);
        self.$toast.error("Couldn't fill in the payroll expenses. Add them by hand.");
      }
    },

    badgeColor,
    badgeLabel,
    formatMyr,
    getDisplayDate(dt) {
      return formatDate(dt);
    },
    getVendorName(item) {
      if (item.vendor && item.vendor.name) {
        return item.vendor.name;
      }
      return "N/A";
    },
    getVendorInitials(vendorName) {
      if (!vendorName || vendorName === "N/A") return "?";
      return vendorName
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    getTruncatedDescription(description) {
      if (!description) return "No description";
      return description.length > 50
        ? description.substring(0, 50) + "..."
        : description;
    },
    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },
    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;

      // Build params for API call
      const params = {
        page: self.currentPage,
        pageSize: self.perPage,
        sortBy: self.sortBy,
        sortDirection: self.sortDirection,
      };

      // Add filters
      if (self.filters.vendorName) {
        params.vendorName = self.filters.vendorName;
      }
      if (self.filters.description) {
        params.description = self.filters.description;
      }
      if (self.filters.status) {
        params.status = self.filters.status;
      }
      if (self.filters.dateFrom) {
        params.dateFrom = self.filters.dateFrom;
      }
      if (self.filters.dateTo) {
        params.dateTo = self.filters.dateTo;
      }

      self.expenseApi
        .getListByCurrentBusiness(params)
        .then((response) => {
          // Handle your actual API response structure
          if (response && response.data) {
            self.items = response.data || [];
            self.totalItems = response.totalRecords || 0;
            self.currentPage = response.page || 1;
            self.totalPages = response.totalPages || 1;
          } else if (response.result && response.result.data) {
            self.items = response.result.data || [];
            self.totalItems = response.result.totalRecords || 0;
            self.currentPage = response.result.page || 1;
            self.totalPages = response.result.totalPages || 1;
          } else {
            // Simple response (current implementation)
            self.items = response.result || response || [];
            self.totalItems = self.items.length;
            self.totalPages = Math.ceil(self.totalItems / self.perPage);
          }

          // Ensure totalPages is at least 1 if there are items
          if (self.totalItems > 0 && self.totalPages < 1) {
            self.totalPages = 1;
          }


          self.loading = false;
        })
        .catch((error) => {
          console.error("Failed to load expenses:", error);
          self.loadError = true;
          self.items = [];
          self.totalItems = 0;
          self.totalPages = 1;
          self.loading = false;
        });
    },
    onDuplicate(item) {
      var self = this;
      self.$router.push({
        path: `/tenants/OtherExpense/${item.id}/duplicate`,
      });
    },
    onEdit(item) {
      var self = this;
      self.$router.push({
        path: `/tenants/OtherExpense/${item.id}`,
      });
    },

    showDeleteConfirmation(item) {
      var self = this;
      self.itemToDelete = item;
      self.warningModal = true;
    },
    confirmDelete() {
      if (this.deleting) return;
      this.deleting = true;
      this.expenseApi
        .delete(this.itemToDelete.id)
        .then(() => {
          this.refreshTable();
          this.fetchSummary();
          this.$toast.success("Expense deleted");
          this.warningModal = false;
        })
        .catch((error) => {
          console.error("Delete error:", error);
          this.$toast.error("Couldn't delete the expense. " + helper.getErrorMessage(error && error.data, "Please try again."));
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    onDeleteConfirmation(status, evt, accept) {
      if (!status) {
        this.itemToDelete = {};
      }
    },
    addNew() {
      this.$router.push({ path: "/tenants/otherExpense" });
    },
    // Load status options from API
    loadStatusOptions() {
      var self = this;
      self.expenseApi
        .getStatusTypes()
        .then((response) => {
          self.statusOptions = response.result;
        })
        .catch((error) => {
          console.error("Error loading status options:", error);
          // Fallback to static options if API fails
          self.statusOptions = [
            { id: 0, name: "Draft" },
            { id: 1, name: "Approve" },
            { id: 2, name: "Paid" },
            { id: 3, name: "Cancelled" },
          ];
        });
    },
    // Org-wide expense pull for real analytics (KPIs, distribution, trend).
    // The paginated table uses `items`; analytics use `allExpenses`.
    fetchSummary() {
      this.expenseApi
        .getListByCurrentBusiness({ page: 1, pageSize: 1000 })
        .then((r) => {
          this.allExpenses =
            (r && (r.result?.data ?? r.data ?? r.result)) || [];
        })
        .catch(() => {
          /* analytics degrade gracefully; the table still works */
        });
    },
    // Pagination event handlers
    onPaginationChange(paginationData) {
      if (paginationData !== this.perPage) {
        this.perPage = parseInt(paginationData);
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    onPageChange(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.refreshTable();
      }
    },
    onItemsPerPageChange(itemsPerPage) {
      if (itemsPerPage !== this.perPage) {
        this.perPage = parseInt(itemsPerPage);
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    onSorterChange(sorter) {
      if (sorter && sorter.column) {
        this.sortBy = sorter.column;
        this.sortDirection = sorter.asc ? "asc" : "desc";
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    // Filter methods
    resetFilters() {
      this.filters = {
        vendorName: "",
        description: "",
        status: "",
        dateFrom: "",
        dateTo: "",
      };
      this.currentPage = 1;
      this.refreshTable();
    },
    applyFilters() {
      this.currentPage = 1;
      this.refreshTable();
      if (window.innerWidth >= 768) {
        this.showFilters = false;
      }
    },
    // Quick actions (both post to the ledger, so they confirm first)
    askAction(kind, item) {
      this.actionConfirm = { show: true, kind, item, busy: false };
    },
    async runAction() {
      const { kind, item } = this.actionConfirm;
      if (!item || this.actionConfirm.busy) return;
      this.actionConfirm.busy = true;
      if (kind === "pay") await this.markAsPaid(item);
      else await this.quickApprove(item);
      this.actionConfirm = { show: false, kind: "", item: null, busy: false };
    },
    async quickApprove(item) {
      try {
        await this.expenseApi.quickApprove(item.id);
        this.$toast.success("Expense approved");
        this.refreshTable();
        this.fetchSummary();
      } catch (error) {
        this.$toast.error("Couldn't approve the expense. " + helper.getErrorMessage(error && error.data, "Please try again."));
      }
    },
    async markAsPaid(item) {
      try {
        await this.expenseApi.markAsPaid(item.id);
        this.$toast.success("Expense marked as paid");
        this.refreshTable();
        this.fetchSummary();
      } catch (error) {
        this.$toast.error("Couldn't mark the expense as paid. " + helper.getErrorMessage(error && error.data, "Please try again."));
      }
    },

    // Bulk Entry Methods
    async loadBulkEntryData() {
      try {
        // Load quick vendors and all vendors
        const [quickVendors, allVendors] = await Promise.all([
          this.expenseApi.getQuickVendors(),
          this.vendorApi.getListByCurrentBusiness() // You might need to implement this
        ])


        this.quickVendors = quickVendors.result || []

        this.allVendors = allVendors.result || []
      } catch (error) {
        console.error('Error loading bulk entry data:', error)
        // Fallback to just quick vendors
        try {
          this.quickVendors = await this.expenseApi.getQuickVendors()
        } catch (fallbackError) {
          console.error('Error loading quick vendors:', fallbackError)
        }
      }
    },
    
    addBulkRow() {
      this.bulkExpenses.push({
        date: mytToday(), // Today's date in Malaysia
        vendorId: '',
        amount: '',
        description: ''
      })
    },
    
    removeBulkRow(index) {
      this.bulkExpenses.splice(index, 1)
    },
    
    clearAllRows() {
      this.bulkConfirm = 'clear'
    },

    onBulkConfirmShow(visible) {
      if (!visible) this.bulkConfirm = null
    },

    confirmBulkDiscard() {
      const closing = this.bulkConfirm === 'close'
      this.bulkExpenses = []
      this.bulkConfirm = null
      if (closing) this.showBulkEntryModal = false
    },
    
    onVendorChange(index) {
      const expense = this.bulkExpenses[index]
      const selectedVendor = this.quickVendors.find(v => v.vendorId === expense.vendorId)
      
      // Auto-suggest amount based on vendor's average
      if (selectedVendor && !expense.amount) {
        expense.amount = selectedVendor.averageAmount.toFixed(2)
      }
      
      // Auto-suggest description if empty
      if (selectedVendor && !expense.description) {
        const vendorName = selectedVendor.vendorName.toLowerCase()
        if (vendorName.includes('tnb')) {
          expense.description = 'Electricity bill'
        } else if (vendorName.includes('syabas')) {
          expense.description = 'Water bill'
        } else if (vendorName.includes('petronas') || vendorName.includes('shell')) {
          expense.description = 'Fuel expense'
        } else {
          expense.description = `Payment to ${selectedVendor.vendorName}`
        }
      }
    },
    
    hasRowErrors(index) {
      const expense = this.bulkExpenses[index]
      return !expense.date || !expense.vendorId || !expense.amount || parseFloat(expense.amount) <= 0
    },
    
    async saveBulkExpenses() {
      if (this.validEntries === 0) {
        this.$toast.error('Add a date, vendor and amount to at least one row.')
        return
      }
      
      this.isSaving = true
      
      try {
        // Filter only valid entries
        const validExpenses = this.bulkExpenses
          .filter(expense => 
            expense.date && 
            expense.vendorId && 
            expense.amount && 
            parseFloat(expense.amount) > 0
          )
          .map(expense => ({
            date: expense.date,
            vendorId: expense.vendorId,
            totalAmount: parseFloat(expense.amount),
            description: expense.description || 'Bulk entry expense'
          }))
        
        const result = await this.expenseApi.bulkCreate(validExpenses)
        
        // Show success message
        if (result.errors && result.errors.length > 0) {
          this.$toast.warning(
            `${result.successfullyCreated} expenses created. ${result.errors.length} couldn't be saved. Check them and try again.`
          )
        } else {
          this.$toast.success(`${result.successfullyCreated} expenses created`)
        }
        
        // Close modal and refresh the expense list
        this.showBulkEntryModal = false
        this.bulkExpenses = []

        // Emit event to refresh parent expense list
        this.$emit('expenses-created', result)

        // Refresh table + org-wide analytics
        this.refreshTable()
        this.fetchSummary()

      } catch (error) {
        console.error('Error saving bulk expenses:', error)
        this.$toast.error(`Couldn't create the expenses. ${helper.getErrorMessage(error && error.data, 'Please try again.')}`)
      } finally {
        this.isSaving = false
      }
    },
    
    // Closing the bulk modal: ask before throwing away typed-in rows
    onBulkModalClose(visible) {
      if (visible) return
      if (this.isSaving) return
      if (this.hasBulkContent) {
        this.bulkConfirm = 'close'
        return
      }
      this.bulkExpenses = []
      this.showBulkEntryModal = false
    }
  },
};
</script>

<style scoped>
/* Fix dropdown z-index issues */
.dropdown-with-zindex {
  position: static;
}

.dropdown-with-zindex .dropdown-menu {
  z-index: 1050 !important;
  position: absolute;
  will-change: transform;
}

.dropdown-mobile .dropdown-menu {
  z-index: 1055 !important;
  position: absolute;
  will-change: transform;
}

/* Enhanced action buttons */
.action-buttons,
.action-buttons .btn-group {
  position: relative;
  z-index: auto;
}

/* Lift the open row above its neighbours so the dropdown menu always
   renders on top instead of behind the rows below it. */
.action-buttons:has(.dropdown.show),
.action-buttons .btn-group:has(.dropdown.show) {
  z-index: 1060;
}

.action-buttons .dropdown-toggle::after {
  margin-left: 0.5em;
}

/* Vendor avatar styling */
.vendor-avatar {
  flex-shrink: 0;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

/* Improve mobile responsiveness */
@media (max-width: 575.98px) {
  .btn-group {
    flex-wrap: wrap;
  }

  .btn-group .btn {
    margin-bottom: 0.25rem;
  }
}

/* Custom styling for mobile cards */
@media (max-width: 991.98px) {
  .d-none.d-lg-block {
    display: none !important;
  }
}

@media (min-width: 992px) {
  .d-lg-none {
    display: none !important;
  }
}



/* Loading spinner center alignment */
.spinner-border {
  width: 2rem;
  height: 2rem;
}

/* Badge improvements */
.badge {
  font-size: 0.75em;
  font-weight: 500;
  letter-spacing: 0.025em;
}

/* Better spacing for mobile pagination */
.pagination-sm .page-link {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* Table responsive improvements */
.table-responsive {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

/* Card hover effects for mobile */
@media (max-width: 991.98px) {
  .card {
    border: 1px solid #dee2e6;
  }
}

/* Improve dropdown menu appearance */
.dropdown-menu {
  border: 1px solid #dee2e6;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
  border-radius: 0.375rem;
}

.dropdown-item {
  padding: 0.5rem 1rem;
  transition: background-color 0.15s ease-in-out;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
}

/* Button group improvements */
.btn-group .btn {
  border-color: #dee2e6;
}

.btn-group .btn:hover {
  border-color: #adb5bd;
  z-index: 1;
}

/* Alert improvements */
.alert {
  border: 1px solid #e2e8f0;
}

.alert-info {
  background-color: #f1f6fb;
  border-color: #dbe7f3;
  color: #1e3a5f;
}

/* Bulk Entry Styles */
.table-responsive {
  max-height: 400px;
  overflow-y: auto;
}

.table th {
  background-color: #f8f9fa;
  border-top: none;
  font-weight: 600;
  font-size: 0.875rem;
  position: sticky;
  top: 0;
  z-index: 10;
}

.table td {
  vertical-align: middle;
  padding: 8px;
}

.form-control-sm {
  font-size: 0.875rem;
}

.is-invalid {
  border-color: #dc3545;
}

.table-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

/* Summary card styling */
.summary-stat {
  text-align: center;
}

.summary-stat h4 {
  font-weight: 700;
  margin-bottom: 4px;
}

.summary-stat small {
 
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Modal styling */
.modal-xl .modal-dialog {
  max-width: 90%;
}

@media (max-width: 768px) {
  .modal-xl .modal-dialog {
    max-width: 95%;
    margin: 10px;
  }

  .table-responsive {
    font-size: 0.8rem;
  }
}

/* ============================================================= */
/* Brand redesign — navy #1E3A5F / emerald #10B981               */
/* ============================================================= */
h1 { color: #1e3a5f; letter-spacing: -0.01em; }

/* Buttons → brand */
::v-deep .btn-primary {
  background-color: #10b981;
  border-color: #10b981;
}
::v-deep .btn-primary:hover,
::v-deep .btn-primary:focus,
::v-deep .btn-primary:not(:disabled):not(.disabled):active {
  background-color: #059669;
  border-color: #059669;
}
::v-deep .btn-outline-primary {
  color: #1e3a5f;
  border-color: #1e3a5f;
}
::v-deep .btn-outline-primary:hover,
::v-deep .btn-outline-primary.active,
::v-deep .btn-outline-primary:not(:disabled):not(.disabled).active {
  background-color: #1e3a5f;
  border-color: #1e3a5f;
  color: #fff;
}
::v-deep .badge-success { background-color: #10b981; }

/* KPI tiles */
.inv-kpi {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  height: 100%;
}
.inv-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.inv-kpi-label { font-size: 0.72rem; font-weight: 600; letter-spacing: 0.05em; color: #64748b; }
.inv-kpi-chip { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; font-size: 0.95rem; }
.chip-navy { background: rgba(30, 58, 95, 0.1); color: #1e3a5f; }
.chip-success { background: rgba(16, 185, 129, 0.13); color: #059669; }
.chip-warning { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.chip-danger { background: rgba(239, 68, 68, 0.11); color: #ef4444; }
.chip-secondary { background: rgba(148, 163, 184, 0.16); color: #64748b; }
.inv-kpi-value { font-size: 1.5rem; font-weight: 800; color: #1e3a5f; line-height: 1.1; letter-spacing: -0.02em; }
.inv-kpi-sub { font-size: 0.74rem; color: #94a3b8; margin-top: 0.35rem; }
.inv-kpi-sub.trend-up { color: #059669; }
.inv-kpi-sub.trend-down { color: #ef4444; }

/* Filtered Results Summary */
.inv-summary-card { border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: none; }
.inv-summary-title { font-size: 0.78rem; font-weight: 700; letter-spacing: 0.04em; color: #1e3a5f; }
.badge-soft { background: #eef2f7; color: #1e3a5f; font-weight: 600; }
.inv-stat { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 10px; padding: 0.6rem 0.7rem; height: 100%; }
.inv-stat-mini { padding: 0.15rem; }
.inv-stat-label { font-size: 0.68rem; letter-spacing: 0.03em; color: #94a3b8; }
.inv-stat-val { font-size: 0.95rem; font-weight: 700; color: #334155; }
.inv-stat-meta { font-size: 0.66rem; color: #94a3b8; }
.text-brand-navy { color: #1e3a5f !important; }
.text-success-strong { color: #059669 !important; }
.text-danger-strong { color: #ef4444 !important; }
.text-warn { color: #b45309 !important; }

/* Table polish */
::v-deep .table thead th {
  font-size: 0.72rem;
 
  letter-spacing: 0.03em;
  color: #64748b;
  border-top: none;
  border-bottom: 1px solid #e2e8f0;
}
::v-deep .table tbody td { vertical-align: middle; }
::v-deep .table.table-hover tbody tr:hover { background: #f8fafc; }
</style>